import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Link,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Avatar,
  Modal,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DateRangeIcon from '@mui/icons-material/DateRange';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import BusinessIcon from '@mui/icons-material/Business';
import WorkOutline from '@mui/icons-material/Business';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import toast from 'react-hot-toast';

import { StatCard } from './components/StatCard';
import { DetailField } from './components/DetailField';
import MilestoneSection from './components/milestones/MilestoneSection';
import TimesheetSection from './components/timesheets/TimesheetSection';
import DocumentsSection from './components/DocumentsSection';
import SOWEditForm from './components/form/SOWEditForm';
import httpClient from 'utils/httpClient';
import { getInitials, toTitleCase } from 'views/Dashboard/common/utils';

const STATUS_COLORS = {
  draft: {
    borderColor: '#C4C4C4',
    color: '#6B7280',
    backgroundColor: '#F5F5F5',
  },
  in_progress: {
    borderColor: '#A855F7',
    color: '#6B21A8',
    backgroundColor: '#F3E8FF',
  },
  active: {
    borderColor: '#4ADE80',
    color: '#166534',
    backgroundColor: '#DCFCE7',
  },
  signed: {
    borderColor: '#38BDF8',
    color: '#0E7490',
    backgroundColor: '#E0F2FE',
  },
  cancelled: {
    borderColor: '#FCA5A5',
    color: '#B91C1C',
    backgroundColor: '#FEE2E2',
  },
};

const getStatusStyles = (status) => {
  const normalized = status?.toLowerCase();
  return (
    STATUS_COLORS[normalized] || {
      borderColor: '#CBD5F5',
      color: '#374151',
      backgroundColor: '#F3F4F6',
    }
  );
};

const formatCurrency = (value, currencyCode = 'INR') => {
  if (value === undefined || value === null || Number.isNaN(Number(value)))
    return '—';

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currencyCode || 'INR',
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(Number(value));
  } catch (error) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(Number(value));
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  // Show date and time (24h) without seconds
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const formatMetricValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  return value;
};

const SOWDetailView = () => {
  const { sowId } = useParams();
  const [sow, setSow] = useState(null);
  const [milestoneSummary, setMilestoneSummary] = useState({
    total: 0,
    completed: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [documentsRefreshToken, setDocumentsRefreshToken] = useState(0);
  const [documentsResetToken, setDocumentsResetToken] = useState(0);

  const fetchSow = useCallback(async () => {
    if (!sowId) {
      return;
    }

    try {
      setLoading(true);
      const response = await httpClient.get(
        `api/v1/project-management/sows/${sowId}/`
      );

      const { status, message, data } = response || {};

      if (status === false) {
        throw new Error(message || 'Failed to load SOW details');
      }

      const payload = data ?? response;
      setSow(payload || null);
      setError(null);

      const initialTotal =
        payload?.budget_model === 'hourly'
          ? 0
          : Number(payload?.milestone_count);
      setMilestoneSummary({
        total: Number.isFinite(initialTotal) ? initialTotal : 0,
        completed: 0,
      });
    } catch (err) {
      console.error('Error fetching SOW:', err);
      const message = err?.message || 'Failed to load SOW details';
      setError(message);
      setMilestoneSummary({ total: 0, completed: 0 });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [sowId]);

  useEffect(() => {
    if (!sowId) {
      setError('SOW ID missing in URL');
      setLoading(false);
      return;
    }

    fetchSow();
  }, [sowId, fetchSow]);

  const handleMilestonesSummaryChange = useCallback(
    ({
      milestoneCount,
      totalClientAmount,
      totalFreelancerAmount,
      completedCount,
    }) => {
      setSow((prevSow) => {
        if (!prevSow) {
          return prevSow;
        }

        const nextSow = { ...prevSow };

        if (typeof milestoneCount === 'number') {
          nextSow.milestone_count = milestoneCount;
        }

        if (typeof totalClientAmount === 'number') {
          nextSow.total_billable_amount = totalClientAmount;
        }

        if (typeof totalFreelancerAmount === 'number') {
          nextSow.total_freelancer_payout = totalFreelancerAmount;
        }

        return nextSow;
      });

      setMilestoneSummary((prev) => ({
        total: typeof milestoneCount === 'number' ? milestoneCount : prev.total,
        completed:
          typeof completedCount === 'number' ? completedCount : prev.completed,
      }));
    },
    []
  );

  // Handle document updates without full SOW refetch
  const updateDocuments = useCallback((updatedDocuments) => {
    setSow((prevSow) => {
      if (!prevSow) return prevSow;

      const newSow = { ...prevSow };

      // Update client document if provided
      if (updatedDocuments.client_document) {
        newSow.client_document = {
          ...prevSow.client_document,
          ...updatedDocuments.client_document,
        };
      }

      // Update freelancer document if provided
      if (updatedDocuments.freelancer_document) {
        newSow.freelancer_document = {
          ...prevSow.freelancer_document,
          ...updatedDocuments.freelancer_document,
        };
      }

      return newSow;
    });
  }, []);

  // Handle SOW status updates without full refetch
  const updateSowStatus = useCallback((statusData) => {
    setSow((prevSow) => {
      if (!prevSow) return prevSow;

      return {
        ...prevSow,
        ...statusData,
      };
    });
  }, []);

  const triggerDocumentsRefresh = useCallback(() => {
    setDocumentsRefreshToken((prev) => prev + 1);
  }, []);

  const triggerDocumentsReset = useCallback(() => {
    setDocumentsResetToken((prev) => prev + 1);
  }, []);

  // Handle SOW updates (e.g., when reverting to draft)
  const handleSowUpdate = useCallback((updatedData) => {
    setSow((prevSow) => {
      if (!prevSow) return prevSow;

      return {
        ...prevSow,
        ...updatedData,
      };
    });
  }, []);

  // Prepare documents array for DocumentsSection
  const documents = useMemo(() => {
    return [sow?.client_document, sow?.freelancer_document].filter(Boolean);
  }, [sow?.client_document, sow?.freelancer_document]);

  // Handle edit modal open/close
  const handleEditClick = () => {
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
  };

  // Handle successful SOW update
  const handleEditSuccess = useCallback(
    (updatedSow) => {
      // Update local state with the updated SOW data
      setSow((prevSow) => {
        if (!prevSow) return prevSow;
        return {
          ...prevSow,
          ...updatedSow,
          markup_percent: updatedSow.markup_percent ?? prevSow.markup_percent,
          fx_rate: updatedSow.fx_rate ?? prevSow.fx_rate,
          status: updatedSow.status ?? prevSow.status, // Status may revert to DRAFT
        };
      });
      // Optionally refetch the full SOW to ensure all data is up to date
      fetchSow();
    },
    [fetchSow]
  );

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  if (!sow) {
    return (
      <Box p={4}>
        <Alert severity='warning'>SOW not found</Alert>
      </Box>
    );
  }

  const statusStyles = getStatusStyles(sow?.status);
  const isTimesheetView = sow?.budget_model === 'hourly';

  // const paymentTermsLabel = sow?.payment_terms
  //   ? sow.payment_terms === 'milestone_end'
  //     ? 'Milestone End'
  //     : 'Milestone Start'
  //   : '-';

  const billableValue = formatCurrency(sow?.total_billable_amount, 'INR');
  const freelancerPayoutValue = formatCurrency(
    sow?.total_freelancer_payout,
    'INR'
  );

  const approvedMilestoneCount = Number(sow?.approved_milestone_count);
  const totalMilestoneCount = Number(sow?.milestone_count);
  const milestoneCountValue =
    Number.isFinite(approvedMilestoneCount) &&
    Number.isFinite(totalMilestoneCount) &&
    totalMilestoneCount > 0
      ? `${approvedMilestoneCount} of ${totalMilestoneCount} approved`
      : sow?.milestone_count || 0;

  const timesheetCount = Number(sow?.timesheet_count);
  const approvedTimesheetCount = Number(sow?.approved_timesheet_count);
  const rejectedTimesheetCount = Number(sow?.rejected_timesheet_count);
  const clockedHoursValue = formatMetricValue(sow?.clocked_hours);
  const approvedHoursValue = formatMetricValue(sow?.approved_hours);
  const hoursBreakdownValue =
    clockedHoursValue === '—' && approvedHoursValue === '—'
      ? '—'
      : `${clockedHoursValue} clocked • ${approvedHoursValue} approved`;

  const completedMilestones = Number.isFinite(approvedMilestoneCount)
    ? approvedMilestoneCount
    : milestoneSummary.completed || 0;
  const totalMilestones = Number.isFinite(totalMilestoneCount)
    ? totalMilestoneCount
    : milestoneSummary.total || 0;
  const progressPercentage =
    totalMilestones > 0
      ? Math.round((completedMilestones / totalMilestones) * 100)
      : 0;

  return (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='start'
        mb={2}
      >
        <Box>
          <Box display='flex' alignItems='center' marginBottom={2}>
            <Box display='flex' alignItems='center' gap={0.5}>
              <Typography
                variant='h4'
                onClick={() => {
                  if (sow?.id) {
                    navigator.clipboard.writeText(sow.id).then(() => {
                      toast.success('SOW ID copied to clipboard');
                    });
                  }
                }}
                sx={{
                  cursor: sow?.id ? 'pointer' : 'default',
                  '&:hover': sow?.id ? { opacity: 0.7 } : {},
                }}
              >
                {`SOW v${sow?.version || 1}`}
              </Typography>
              {sow?.id && (
                <Tooltip title='Copy SOW ID'>
                  <IconButton
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(sow.id).then(() => {
                        toast.success('SOW ID copied to clipboard');
                      });
                    }}
                    sx={{
                      p: 0.25,
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                    aria-label='Copy SOW ID to clipboard'
                  >
                    <ContentCopyIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Chip
              label={sow?.status || '—'}
              size='small'
              variant='outlined'
              sx={{ mx: 1, textTransform: 'capitalize', ...statusStyles }}
            />
          </Box>
        </Box>

        <Button
          variant='text'
          startIcon={<EditIcon />}
          onClick={handleEditClick}
          disabled={loading}
        >
          Edit
        </Button>
      </Box>

      {isTimesheetView ? (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label='Billable Amount (Gross)'
              value={billableValue}
              icon={<AccountBalanceWalletIcon fontSize='inherit' />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label='Freelancer Payout'
              value={freelancerPayoutValue}
              icon={<AccountBalanceWalletIcon fontSize='inherit' />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label='Hours'
              value={hoursBreakdownValue}
              icon={<DateRangeIcon fontSize='inherit' />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label='Total Timesheets'
              value={Number.isFinite(timesheetCount) ? timesheetCount : '—'}
              icon={<WorkHistoryIcon fontSize='inherit' />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label='Approved'
              value={
                Number.isFinite(approvedTimesheetCount)
                  ? approvedTimesheetCount
                  : '—'
              }
              icon={<DataUsageIcon fontSize='inherit' />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label='Rejected'
              value={
                Number.isFinite(rejectedTimesheetCount)
                  ? rejectedTimesheetCount
                  : '—'
              }
              icon={<DataUsageIcon fontSize='inherit' />}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label='Billable Amount (Gross)'
              value={billableValue}
              icon={<AccountBalanceWalletIcon fontSize='inherit' />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label='Freelancer Payout'
              value={freelancerPayoutValue}
              icon={<AccountBalanceWalletIcon fontSize='inherit' />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label='Milestones'
              value={milestoneCountValue}
              subLabel={
                totalMilestones > 0
                  ? `${progressPercentage}% complete`
                  : undefined
              }
              icon={<DataUsageIcon fontSize='inherit' />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label='Estimated Total Hours'
              value={sow?.estimated_total_hours || 0}
              icon={<DateRangeIcon fontSize='inherit' />}
            />
          </Grid>
        </Grid>
      )}

      <Paper sx={{ borderRadius: 2, p: 3, mb: 3 }}>
        <Typography variant='subtitle1' fontWeight={600} mb={2}>
          SOW Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  mr: 1.5,
                  fontSize: '0.875rem',
                  bgcolor: '#E0F2FE',
                  color: '#0369A1',
                }}
              >
                <WorkOutline sx={{ fontSize: 18 }} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='subtitle1'
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  noWrap
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {sow?.metadata?.gig_title || 'N/A'}
                  </span>
                  {sow?.gig_id && (
                    <Link
                      href={`/gig/${sow.gig_id}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      sx={{
                        ml: 0.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: '#2a3547',
                      }}
                      aria-label='Open gig in new tab'
                    >
                      <OpenInNewIcon fontSize='small' />
                    </Link>
                  )}
                </Typography>
                <Typography variant='caption' sx={{ color: '#677084' }} noWrap>
                  {sow?.gig_type ? toTitleCase(sow.gig_type) : 'N/A'}
                  {sow?.budget_model && sow.budget_model !== 'outcome' ? (
                    <>
                      &nbsp;•&nbsp;
                      {toTitleCase(sow.budget_model)}
                    </>
                  ) : null}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  mr: 1.5,
                  fontSize: '0.875rem',
                  bgcolor: '#E8EDFF',
                  color: '#1D4ED8',
                }}
              >
                <BusinessIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='subtitle1'
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  noWrap
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {sow?.company_legal_name || 'N/A'}
                  </span>
                  {sow?.company_id && (
                    <Link
                      href={`/client/${sow.company_id}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      sx={{
                        ml: 0.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: '#2a3547',
                      }}
                      aria-label='Open client in new tab'
                    >
                      <OpenInNewIcon fontSize='small' />
                    </Link>
                  )}
                </Typography>
                <Typography variant='caption' sx={{ color: '#677084' }} noWrap>
                  Margin Segment: {sow?.margin_segment || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
              <Avatar
                src={sow?.freelancer?.profile_picture}
                sx={{
                  width: 40,
                  height: 40,
                  mr: 1.5,
                  fontSize: '0.875rem',
                  bgcolor: '#FEF3C7',
                  color: '#D97706',
                }}
              >
                {getInitials(sow?.freelancer?.name)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='subtitle1'
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  noWrap
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {sow?.freelancer_name || 'N/A'}
                  </span>
                  {sow?.freelancer_id && (
                    <Link
                      href={`/freelancer/${sow.freelancer_id}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      sx={{
                        ml: 0.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: '#2a3547',
                      }}
                      aria-label='Open freelancer profile in new tab'
                    >
                      <OpenInNewIcon fontSize='small' />
                    </Link>
                  )}
                </Typography>
                <Typography variant='caption' sx={{ color: '#677084' }} noWrap>
                  {sow?.metadata?.freelancer_role || '—'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailField
              label='Created Date'
              value={formatDate(sow.created_at)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailField
              label='Updated Date'
              value={formatDate(sow.updated_at)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailField
              label='Project Start'
              value={formatDate(sow.project_start_date)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailField
              label='Project End'
              value={formatDate(sow.project_end_date)}
            />
          </Grid>
        </Grid>
      </Paper>

      {isTimesheetView ? (
        <TimesheetSection projectId={sow?.project_id} />
      ) : (
        <MilestoneSection
          sowId={sowId}
          sow={sow}
          projectId={sow?.project_id}
          onMilestonesChange={handleMilestonesSummaryChange}
          onSowUpdate={handleSowUpdate}
          onDocumentsRefresh={triggerDocumentsRefresh}
          onDocumentsReset={triggerDocumentsReset}
        />
      )}

      <DocumentsSection
        sowId={sowId}
        sowData={sow}
        projectId={sow?.project_id}
        documents={documents}
        refreshToken={documentsRefreshToken}
        resetToken={documentsResetToken}
        onDocumentsUpdate={updateDocuments}
        onSowStatusUpdate={updateSowStatus}
      />

      {/* <Box>
        <Typography variant='subtitle1' fontWeight={600} mb={2}>
          SOW Timeline
        </Typography>
        <Box p={2} sx={{ borderRadius: 2 }} border='1px solid #ddd'>
          <Typography variant='body2' color='text.secondary'>
            Timeline view for milestones will be displayed here once milestones
            are created.
          </Typography>
        </Box>
      </Box> */}

      {/* Edit SOW Modal */}
      <Modal open={editModalOpen} onClose={handleEditClose}>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(2px)',
            p: 2,
          }}
        >
          {sow && (
            <SOWEditForm
              sow={sow}
              onClose={handleEditClose}
              onSuccess={handleEditSuccess}
              onSowUpdate={handleSowUpdate}
              onDocumentsReset={triggerDocumentsReset}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default SOWDetailView;
